/**
 * AdminCentroAccesoPage — Centralized CEO impersonation hub.
 *
 * One page · three tabs (Gestores, Agentes de Pago, Proveedores de Tickets).
 * Each row exposes a single "Entrar" button that issues a 30-min read-only
 * preview JWT and opens the corresponding panel in a new tab. The preview
 * banner + global write-block middleware are handled by existing layout +
 * `block_preview_writes` so the CEO never accidentally mutates state while
 * inspecting a panel.
 *
 * Dependencies (already implemented in earlier iterations):
 *   • POST /api/admin/gestores/{id}/preview-token
 *   • POST /api/admin/providers/{role}/{id}/preview-token
 *   • /preview/launch?token=… (GestorPreviewLaunchPage routes by role claim)
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { toast } from 'sonner';
import {
  Users, Truck, Ticket, Search, ExternalLink, ShieldCheck, RefreshCw, Loader2,
  Eye, FlaskConical, Trash2,
} from 'lucide-react';
import axios from 'axios';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TABS = [
  {
    key: 'gestor',
    label: 'Gestores',
    icon: Users,
    color: 'text-blue-600',
    accent: 'bg-blue-50 text-blue-700 ring-blue-200',
    description: 'Gestores comerciales de la red. Accedé en modo solo-lectura para auditar sus paneles, módulos habilitados y cuenta corriente.',
  },
  {
    key: 'payment_agent',
    label: 'Proveedores de Monedas',
    icon: Truck,
    color: 'text-emerald-600',
    accent: 'bg-emerald-50 text-emerald-700 ring-emerald-200',
    description: 'Agentes de pago (cash-out en destino). Vista previa de su tablero operativo: cola, disponibilidad y liquidez declarada.',
  },
  {
    key: 'ticket_provider',
    label: 'Proveedores de Pasajes',
    icon: Ticket,
    color: 'text-purple-600',
    accent: 'bg-purple-50 text-purple-700 ring-purple-200',
    description: 'Mayoristas/proveedores de tickets aéreos. Acceso al panel donde gestionan PNRs, asignaciones y métodos de pago.',
  },
];

const AdminCentroAccesoPage = () => {
  const { getAuthHeaders } = useAuth();
  const [active, setActive] = useState('gestor');
  const [search, setSearch] = useState('');
  const [data, setData] = useState({ gestor: [], payment_agent: [], ticket_provider: [] });
  const [loading, setLoading] = useState(true);
  const [enteringId, setEnteringId] = useState(null);
  const [modeDialog, setModeDialog] = useState(null); // {kind, item}
  const [simSummary, setSimSummary] = useState(null); // {by_collection, total}
  const [purging, setPurging] = useState(false);

  const loadSimSummary = useCallback(async () => {
    try {
      const r = await axios.get(`${API_URL}/admin/simulations/summary`, { headers: getAuthHeaders() });
      setSimSummary(r.data);
    } catch {
      setSimSummary(null);
    }
  }, [getAuthHeaders]);

  const purgeSimulations = async () => {
    if (!window.confirm('¿Borrar TODAS las simulaciones (todas las colecciones)? Esta acción no se puede deshacer.')) return;
    setPurging(true);
    try {
      const r = await axios.delete(`${API_URL}/admin/simulations/purge`, { headers: getAuthHeaders() });
      toast.success(`Eliminadas ${r.data.total_deleted} filas de simulación`);
      await loadSimSummary();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'No se pudo purgar');
    } finally {
      setPurging(false);
    }
  };

  const loadAll = useCallback(async () => {
    setLoading(true);
    try {
      const headers = getAuthHeaders();
      const [g, pa, tp] = await Promise.all([
        axios.get(`${API_URL}/gestores`, { headers }),
        axios.get(`${API_URL}/providers?role=payment_agent`, { headers }),
        axios.get(`${API_URL}/providers?role=ticket_provider`, { headers }),
      ]);
      setData({
        gestor: g.data || [],
        payment_agent: pa.data?.providers || pa.data || [],
        ticket_provider: tp.data?.providers || tp.data || [],
      });
    } catch (e) {
      toast.error('No se pudo cargar el directorio de acceso');
    } finally {
      setLoading(false);
    }
  }, [getAuthHeaders]);

  useEffect(() => { loadAll(); loadSimSummary(); }, [loadAll, loadSimSummary]);

  const enter = async (kind, item, mode) => {
    setEnteringId(item.id);
    setModeDialog(null);
    try {
      const headers = getAuthHeaders();
      const endpoint = kind === 'gestor'
        ? `${API_URL}/admin/gestores/${item.id}/preview-token`
        : `${API_URL}/admin/providers/${kind}/${item.id}/preview-token`;
      const r = await axios.post(endpoint, { mode }, { headers });
      if (!r.data?.token) throw new Error('sin token');
      const url = `${window.location.origin}/preview/launch?token=${encodeURIComponent(r.data.token)}`;
      window.open(url, '_blank', 'noopener,noreferrer');
      toast.success(mode === 'simulation'
        ? 'Modo Simulación abierto — tus acciones quedarán marcadas como simulación'
        : 'Acceso solo-lectura abierto en otra pestaña');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'No se pudo abrir la vista previa');
    } finally {
      setEnteringId(null);
    }
  };

  const filtered = useMemo(() => {
    const q = (search || '').toLowerCase().trim();
    const list = data[active] || [];
    if (!q) return list;
    return list.filter((x) => {
      const hay = [x.name, x.email, x.phone, x.country, x.status].filter(Boolean).join(' ').toLowerCase();
      return hay.includes(q);
    });
  }, [data, active, search]);

  const tabMeta = TABS.find((t) => t.key === active);

  return (
    <div className="space-y-5" data-testid="centro-acceso-page">
      {/* Header */}
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
            <ShieldCheck className="w-6 h-6 text-emerald-600" />
            Centro de Acceso CEO
          </h1>
          <p className="text-xs text-slate-500 mt-1">
            Entrá a cualquier panel de la red en modo <strong>solo-lectura</strong>. Las escrituras quedan bloqueadas por el middleware preview.
          </p>
        </div>
        <div className="flex items-center gap-2">
          {simSummary && simSummary.total > 0 && (
            <Button
              variant="outline"
              size="sm"
              onClick={purgeSimulations}
              disabled={purging}
              className="border-amber-300 text-amber-700 hover:bg-amber-50"
              data-testid="centro-purge-simulations"
            >
              {purging ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Trash2 className="w-4 h-4 mr-1" />}
              Limpiar simulaciones ({simSummary.total})
            </Button>
          )}
          <Button variant="outline" size="sm" onClick={() => { loadAll(); loadSimSummary(); }} disabled={loading} data-testid="centro-acceso-refresh">
            <RefreshCw className={`w-4 h-4 mr-1 ${loading ? 'animate-spin' : ''}`} /> Refrescar
          </Button>
        </div>
      </div>

      <Tabs value={active} onValueChange={setActive} className="w-full">
        <TabsList className="grid w-full grid-cols-3 mb-3">
          {TABS.map((t) => {
            const Icon = t.icon;
            const count = (data[t.key] || []).length;
            return (
              <TabsTrigger key={t.key} value={t.key} data-testid={`centro-tab-${t.key}`} className="gap-2">
                <Icon className={`w-4 h-4 ${t.color}`} />
                <span className="truncate">{t.label}</span>
                <Badge variant="secondary" className="ml-1">{count}</Badge>
              </TabsTrigger>
            );
          })}
        </TabsList>

        {/* Search bar — shared across tabs */}
        <div className="relative mb-3">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <Input
            placeholder={`Buscar en ${tabMeta?.label || 'la lista'}…`}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-9"
            data-testid="centro-acceso-search"
          />
        </div>

        {TABS.map((t) => (
          <TabsContent key={t.key} value={t.key} className="mt-0">
            <Card className={`p-4 ring-1 ${t.accent} mb-3`}>
              <div className="flex items-start gap-3">
                <t.icon className={`w-5 h-5 ${t.color} shrink-0 mt-0.5`} />
                <p className="text-xs leading-relaxed text-slate-700">{t.description}</p>
              </div>
            </Card>

            {loading ? (
              <div className="flex items-center justify-center py-16 text-slate-500">
                <Loader2 className="w-5 h-5 mr-2 animate-spin" /> Cargando…
              </div>
            ) : filtered.length === 0 ? (
              <Card className="p-10 text-center text-slate-500">
                <t.icon className="w-10 h-10 mx-auto mb-3 text-slate-300" />
                <p className="text-sm">
                  {search ? 'Sin resultados para tu búsqueda.' : `Aún no hay ${t.label.toLowerCase()} registrados.`}
                </p>
              </Card>
            ) : (
              <div className="grid gap-2">
                {filtered.map((item) => (
                  <Card
                    key={item.id}
                    className="p-3 flex items-center justify-between gap-3 hover:shadow-md transition-shadow"
                    data-testid={`centro-row-${t.key}-${item.id}`}
                  >
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-semibold text-slate-900 truncate">{item.name || item.email}</span>
                        {t.key === 'gestor' && (
                          <Badge variant={item.is_active ? 'default' : 'secondary'} className="text-xs">
                            {item.is_active ? 'Activo' : 'Inactivo'}
                          </Badge>
                        )}
                        {t.key !== 'gestor' && item.status && (
                          <Badge variant={item.status === 'active' ? 'default' : 'secondary'} className="text-xs">
                            {item.status}
                          </Badge>
                        )}
                        {t.key !== 'gestor' && item.availability && (
                          <Badge variant="outline" className="text-xs">
                            {item.availability}
                          </Badge>
                        )}
                        {t.key !== 'gestor' && item.country && (
                          <Badge variant="outline" className="text-xs">{item.country}</Badge>
                        )}
                      </div>
                      <div className="text-xs text-slate-500 truncate mt-0.5">
                        {item.email}
                        {item.phone ? ` · ${item.phone}` : ''}
                      </div>
                    </div>
                    <Button
                      size="sm"
                      onClick={() => setModeDialog({ kind: t.key, item })}
                      disabled={enteringId === item.id}
                      data-testid={`centro-enter-${t.key}-${item.id}`}
                      className={t.key === 'gestor'
                        ? 'bg-blue-600 hover:bg-blue-700'
                        : t.key === 'payment_agent'
                          ? 'bg-emerald-600 hover:bg-emerald-700'
                          : 'bg-purple-600 hover:bg-purple-700'}
                    >
                      {enteringId === item.id ? (
                        <Loader2 className="w-4 h-4 mr-1.5 animate-spin" />
                      ) : (
                        <ExternalLink className="w-4 h-4 mr-1.5" />
                      )}
                      Entrar
                    </Button>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>
        ))}
      </Tabs>

      {/* Mode picker dialog */}
      {modeDialog && (
        <div
          className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4"
          onClick={() => setModeDialog(null)}
          data-testid="mode-dialog-overlay"
        >
          <Card className="max-w-md w-full p-6 space-y-4" onClick={(e) => e.stopPropagation()} data-testid="mode-dialog">
            <div>
              <h2 className="text-lg font-bold text-slate-900">
                Entrar como {modeDialog.item.name || modeDialog.item.email}
              </h2>
              <p className="text-xs text-slate-500 mt-1">Elegí cómo querés acceder al panel.</p>
            </div>
            <button
              type="button"
              onClick={() => enter(modeDialog.kind, modeDialog.item, 'readonly')}
              className="w-full text-left border rounded-lg p-3 hover:border-slate-400 hover:bg-slate-50 transition-all space-y-1"
              data-testid="mode-readonly"
            >
              <div className="flex items-center gap-2 font-semibold text-slate-900">
                <Eye className="w-4 h-4 text-slate-600" /> Auditoría (solo lectura)
              </div>
              <p className="text-xs text-slate-500">
                Inspeccioná el panel sin tocar nada. Las acciones de escritura están bloqueadas por el middleware.
              </p>
            </button>
            <button
              type="button"
              onClick={() => enter(modeDialog.kind, modeDialog.item, 'simulation')}
              className="w-full text-left border-2 border-amber-400 rounded-lg p-3 hover:bg-amber-50 transition-all space-y-1"
              data-testid="mode-simulation"
            >
              <div className="flex items-center gap-2 font-semibold text-amber-700">
                <FlaskConical className="w-4 h-4" /> Simulación (acciones reales con flag)
              </div>
              <p className="text-xs text-amber-700/80">
                Podés crear ventas, pagos y operaciones como si fueras el usuario. Cada acción queda marcada
                <strong> is_simulation=true</strong> y se excluye de contabilidad, reportes y reconciliación real.
                Después podés purgar todo con un click desde Centro de Acceso.
              </p>
            </button>
            <Button variant="outline" size="sm" className="w-full" onClick={() => setModeDialog(null)} data-testid="mode-cancel">
              Cancelar
            </Button>
          </Card>
        </div>
      )}
    </div>
  );
};

export default AdminCentroAccesoPage;
